import React from "react";
import { DataGrid } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import _ from "lodash";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import { useTranslation } from "@i18n";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { BsExclamationCircle } from "react-icons/bs";
import * as XLSX from "xlsx";
import { convertDate } from "@Services/luxonHelper";
import IconComponent from "@CubeComponents/icons";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import Text from "@CubeComponents/html/Text/Text";
import { getNomenclatureFieldWidthPx } from "@Services/utils/useCustomFields";

export default function FilesTable({
  fileUploadList,
  selectedFileIds,
  setSelectedFileIds,
  gridNomenclatureTemplate,
  progressTemplate,
  gridNameTemplate,
  gridCustomFieldTemplate,
  template,
  gridUploadActionTemplate,
  fileNomenclature,
  isDirectImportFlow,
  gridLocationTemplate,
}: any) {
  const questionFields = template?.flatMap((section: any) => section.questionFields) || [];
  const { t } = useTranslation();

  const tailwindCenterClass = "flex justify-center items-center";

  const handleCellKeyDown = (event: React.KeyboardEvent) => {
    if (event.key == " ") {
      event.stopPropagation();
    }
  };

  const hasActionsColumn = fileUploadList.some(
    (file: any) => !file.isFolder && !!gridUploadActionTemplate(file, file.id),
  );
  const hasLocationColumn = Boolean(isDirectImportFlow && gridLocationTemplate);
  const hasNomenclatureColumn =
    fileNomenclature?.fields?.length > 0 && fileUploadList.some((file: any) => !file.isFolder);
  const getRevisionLabel = (row: any) =>
    row?.matchedDirectImportContainer?.revisionLabel || row?.matchedContainer?.revisionLabel || "";
  const hasRevisionColumn = fileUploadList.some((file: any) => getRevisionLabel(file));
  const hasErrorsColumn = fileUploadList.some((file: any) => !file.isFolder);

  const nomenclatureMinWidth = Math.min(
    640,
    Math.max(
      220,
      (fileNomenclature?.fields ?? []).reduce((sum: number, f: any) => sum + getNomenclatureFieldWidthPx(f) + 8, 24),
    ),
  );

  const columns: any[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 2,
      minWidth: 280,
      sortable: false,
      align: "left",
      filterable: false,
      headerAlign: "left",
      cellClassName: tailwindCenterClass,
      headerClassName: tailwindCenterClass,
      renderCell: (params: any) => (params.row.isBulkEdit ? null : gridNameTemplate(params.row, params.row.id)),
    },
    {
      field: "progress",
      headerName: "Progress",
      flex: 1,
      minWidth: 150,
      sortable: false,
      align: "left",
      filterable: false,
      headerAlign: "left",
      cellClassName: tailwindCenterClass,
      headerClassName: tailwindCenterClass,
      renderCell: (params: any) => (params.row.isBulkEdit ? null : progressTemplate(params.row, params.row.id)),
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 240,
      maxWidth: 280,
      flex: 2,
      sortable: false,
      align: "left",
      filterable: false,
      headerAlign: "left",
      cellClassName: tailwindCenterClass,
      headerClassName: tailwindCenterClass,
      renderCell: (params) => (
        <div onKeyDown={handleCellKeyDown} className="flex justify-start items-center w-full h-full px-2">
          {gridUploadActionTemplate(params.row, params.row.id)}
        </div>
      ),
    },
    {
      field: "uploadLocation",
      headerName: t("Location"),
      flex: 1,
      minWidth: 200,
      sortable: false,
      align: "left",
      filterable: false,
      headerAlign: "left",
      cellClassName: tailwindCenterClass,
      headerClassName: tailwindCenterClass,
      renderCell: (params: any) =>
        params.row.isBulkEdit || !gridLocationTemplate ? null : gridLocationTemplate(params.row, params.row.id),
    },
    {
      field: "nomenclature",
      headerName: "Nomenclature",
      flex: 1,
      minWidth: nomenclatureMinWidth,
      sortable: false,
      align: "left",
      filterable: false,
      headerAlign: "left",
      cellClassName: tailwindCenterClass,
      headerClassName: tailwindCenterClass,
      renderCell: (params) => (params.row.isBulkEdit ? null : gridNomenclatureTemplate(params.row, params.row.id)),
    },
    {
      field: "revision",
      headerName: t("Revision"),
      flex: 1,
      minWidth: 140,
      sortable: false,
      filterable: false,
      align: "left",
      headerAlign: "left",
      cellClassName: tailwindCenterClass,
      headerClassName: tailwindCenterClass,
      renderCell: (params: any) => (params.row.isBulkEdit ? null : getRevisionLabel(params.row)),
    },
  ];

  questionFields.forEach((field: any) => {
    const isMandatory = field.isMandatory;
    const label = (
      <span>
        {field.questionValue}
        {isMandatory && <span className="text-red-500"> *</span>}
      </span>
    );

    columns.push({
      field: field._id,
      headerName: label,
      flex: 1,
      minWidth: 260,
      sortable: false,
      filterable: false,
      resizable: true,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        const rawValue = params.row?.[field._id];
        const fieldDisplayValue = Array.isArray(rawValue)
          ? rawValue.join(", ")
          : rawValue == null || rawValue === ""
            ? ""
            : String(rawValue);
        return (
          <div
            onKeyDown={handleCellKeyDown}
            role="gridcell"
            tabIndex={0}
            className="w-full"
            title={fieldDisplayValue || undefined}
          >
            {gridCustomFieldTemplate(field, params.row.id)}
          </div>
        );
      },
    });
  });

  columns.push({
    field: "errors",
    headerName: t("Errors"),
    flex: 2,
    minWidth: 300,
    sortable: false,
    filterable: false,
    align: "left",
    headerAlign: "left",
    renderCell: (params: any) => {
      const errors: string[] = params.row.errors || [];
      if (!errors.length) return null;
      const errorText = errors.join(", ");
      return (
        <ToolTipComponent position="bottom" content={errorText as any}>
          <div className="flex items-center gap-2 min-w-0 overflow-hidden w-full">
            <div className="shrink-0 flex items-center">
              <BsExclamationCircle className="w-4 h-4 text-[#E05650]" />
            </div>
            <span className="text-[#E05650] text-sm truncate">{errorText}</span>
          </div>
        </ToolTipComponent>
      );
    },
  });

  const columnVisibilityModel = {
    actions: hasActionsColumn,
    uploadLocation: hasLocationColumn,
    nomenclature: hasNomenclatureColumn,
    revision: hasRevisionColumn,
    errors: hasErrorsColumn,
  };

  const getActionLabel = (row: any): string => {
    if (row.uploadAsNewFile) return t("Upload as a New File");
    if (row.uploadAsNewVersion) return t("Upload as New version & override custom fields");
    if (row.uploadAsNewVersionRetainFields) return t("Upload as a new version & retain existing custom field data");
    return "";
  };

  const handleExcelExport = () => {
    const headers: string[] = ["Name", "Progress"];
    if (hasActionsColumn) headers.push(t("Actions"));
    if (isDirectImportFlow && gridLocationTemplate) headers.push(t("Location"));
    if (hasNomenclatureColumn) headers.push("Nomenclature");
    if (hasRevisionColumn) headers.push(t("Revision"));
    questionFields.forEach((field: any) => {
      headers.push(field.questionValue + (field.isMandatory ? " *" : ""));
    });
    if (hasErrorsColumn) headers.push(t("Errors"));

    const rows = fileUploadList
      .filter((row: any) => !row.isBulkEdit)
      .map((row: any) => {
        const rowData: any[] = [
          row.name || "",
          typeof row.progress === "number" ? `${row.progress}%` : (row.progress ?? ""),
        ];
        if (hasActionsColumn) rowData.push(getActionLabel(row));
        if (isDirectImportFlow && gridLocationTemplate) rowData.push(row.uploadLocation || "");
        if (hasNomenclatureColumn) rowData.push(row.nomenclature || "");
        if (hasRevisionColumn) rowData.push(getRevisionLabel(row));
        questionFields.forEach((field: any) => {
          const val = row[field._id];
          if (field.fieldType === "date-time" && val) {
            rowData.push(convertDate(Number(val), "NormalDate"));
          } else {
            rowData.push(Array.isArray(val) ? val.join(", ") : (val ?? ""));
          }
        });
        if (hasErrorsColumn) rowData.push((row.errors || []).join(", "));
        return rowData;
      });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Files");
    XLSX.writeFile(wb, "files_export.xlsx");
  };

  const handleRowSelectionChange = (selectionModel: any) => {
    const idsArray = Array.isArray(selectionModel) ? selectionModel : Array.from(_.get(selectionModel, "ids", []));
    setSelectedFileIds(idsArray);
  };

  function renderFooter({ selectedCount, totalCount, t }: any) {
    return (
      <div className="flex justify-between items-center px-4 py-2 text-sm text-gray-600 border-t border-[color:var(--stroke-primary)] w-full">
        <div>
          {selectedCount} {t("Items Selected")}
        </div>
        <div>
          {totalCount} {t("items")}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col">
      <div className="flex justify-end px-2 py-1 border-b border-[color:var(--stroke-primary)] shrink-0">
        <ToolTipComponent content={t("Export to Excel")}>
          <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={handleExcelExport}>
            <div className="flex gap-1 items-center">
              <IconComponent name="ExcelExportIcon" width="1rem" height="1rem" />
              <Text className="inline">{t("Export")}</Text>
            </div>
          </ButtonComponent>
        </ToolTipComponent>
      </div>
      <Box className="flex-1 min-h-0">
        <DataGrid
          rows={fileUploadList}
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          checkboxSelection
          disableRowSelectionOnClick
          isRowSelectable={(params) => params.row?.id !== 0}
          getRowId={(row) => row.id}
          rowSelectionModel={{ type: "include", ids: new Set(selectedFileIds) }}
          onRowSelectionModelChange={handleRowSelectionChange}
          disableColumnMenu
          showCellVerticalBorder
          showColumnVerticalBorder
          rowHeight={80}
          sx={{
            // Match SlickGrid1 look (cubeComponent/html/SlickGrid1/index.css):
            // borders use --stroke-primary (#C6D8EC), near-white header,
            // 0.875rem text in --text-primary, --selection-primary selection.
            "--DataGrid-rowBorderColor": "var(--stroke-primary)",
            border: "1px solid var(--stroke-primary)",
            color: "var(--text-primary)",
            fontFamily: '"Source Sans Pro", sans-serif',
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#fafcff",
              color: "var(--text-primary)",
              justifyContent: "flex-start",
              alignItems: "center",
              display: "flex",
              textAlign: "left",
              padding: "0.5rem 0.75rem",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "600 !important",
              fontSize: "0.875rem",
              justifyContent: "flex-start",
              textAlign: "left",
              width: "100%",
            },
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              color: "var(--text-primary)",
              fontSize: "0.875rem",
              lineHeight: "1.3rem !important",
              padding: "0.5rem 0.75rem !important",
              overflow: "hidden",
            },
            "& .MuiFormControl-root": { height: "auto !important" },
            "& .MuiDataGrid-row": {
              alignItems: "center",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#e8edf5",
            },
            "& .MuiCheckbox-root": { margin: "auto" },
            "& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover": {
              backgroundColor: "var(--selection-primary) !important",
            },
          }}
          localeText={{
            noRowsLabel: "No data to display.",
          }}
          slots={{
            baseCheckbox: CheckBoxComponent as any,
            footer: renderFooter as any,
          }}
          slotProps={{
            footer: {
              selectedCount: selectedFileIds.length,
              totalCount: fileUploadList.length,
              t,
            } as any,
          }}
        />
      </Box>
    </div>
  );
}

FilesTable.defaultProps = {
  fileUploadList: [],
  template: [],
  fileNomenclature: { fields: [], separator: "-" },
  isDirectImportFlow: false,
  gridLocationTemplate: undefined,
};
